import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import { TextFilterProps } from '@progress/kendo-react-data-tools'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { TradingAccountDto, TradingAccountFilterKey } from 'api/Clients/Types/TradingAccount'
import { PageData } from 'api/Pagination/Types'
import { Pager } from 'components/components/Pager'
import { Dialog } from '@/components/ui/dialog'
import { Modal } from 'components/CustomModal'
import { FilterType } from 'components/Filter'
import {
  SelectClientFilter,
  SelectClientModalOpenEvent,
} from 'components/Filter/ClientFields/SelectClientFilter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import {
  SelectIntroducingBrokerModalOpenEvent,
  SelectIntroducingBrokersFilter,
} from 'components/Filter/IntroducingBrokersFields/SelectIntroducingBrokersFilter'
import { getTradingAccountFilterFields } from 'components/Filter/tradingAccountsFields'
import {
  SelectWalletFilter,
  SelectWalletModalOpenEvent,
} from 'components/Filter/WalletFields/SelectWalletFilter'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilterByFilterId,
  setGridFilters,
} from 'store/grid'
import { fetchCountriesAction, fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchBooksTypeAction,
  fetchLiveTradingAccountGroupsAction,
  fetchPlatformTypesAction,
  fetchTradingAccountGroupTypeAction,
  fetchTradingAccountsLeverageTypesAction,
  fetchTradingAccountStatusAction,
  fetchTradingAccountTypeAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { CustomColumnMenu } from '../../Clients/CustomColumnMenu'
import { SelectClientEvent, SelectClientTable } from '../../Clients/SelectClientTable'
import { IntroducingBrokersModal } from '../../IB/IntroducingBrokersModal'
import { SelectIntroducingBrokerEvent } from '../../IB/IntroducingBrokersTable'
import SelectWalletTable, { SelectWalletEvent } from '../../Wallets/SelectWalletTable'
import { GridColumn, liveTradingAccountColumns } from './liveTradingAccountColumns'
import {
  normalizeTradingAccountsSearchQuery,
  normalizeTradingAccountsSortQuery,
} from './normalizer'

const useMapStateToProps = () => ({
  leverageType: useAppSelector((state) => state.tradingAccounts.leverages),
  platformTypes: useAppSelector((state) => state.tradingAccounts.platformTypes),
  servers: useAppSelector((state) => state.tradingAccounts.allServers),
  bookType: useAppSelector((state) => state.tradingAccounts.bookType),
  tradingAccountStatus: useAppSelector((state) => state.tradingAccounts.tradingAccountStatus),
  tradingAccountGroup: useAppSelector((state) => state.tradingAccounts.tradingAccountLiveGroup),
  tradingAccountType: useAppSelector((state) => state.tradingAccounts.tradingAccountType),
  tradingAccountGroupType: useAppSelector((state) => state.tradingAccounts.tradingAccountGroupType),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  gridState: useAppSelector((state) => state.grid),
  gridFilterByFilterIdSelector: useAppSelector(selectGridFilterByFilterId),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return {
    fetchTradingAccountsLeverageType: async () =>
      dispatch(fetchTradingAccountsLeverageTypesAction(tradingAccountsApiClient)),
    fetchPlatformTypes: async () => dispatch(fetchPlatformTypesAction(tradingAccountsApiClient)),
    fetchServers: async () => dispatch(fetchAllServerAction(tradingAccountsApiClient)),
    fetchBookTypes: async () => dispatch(fetchBooksTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountStatus: async () =>
      dispatch(fetchTradingAccountStatusAction(tradingAccountsApiClient)),
    fetchCountries: async () => dispatch(fetchCountriesAction(identityApiClient)),
    fetchTradingAccountGroups: async () =>
      dispatch(fetchLiveTradingAccountGroupsAction(tradingAccountsApiClient)),
    fetchTradingAccountType: async () =>
      dispatch(fetchTradingAccountTypeAction(tradingAccountsApiClient)),
    fetchTradingAccountGroupType: async () =>
      dispatch(fetchTradingAccountGroupTypeAction(tradingAccountsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}

export const LiveTradingAccounts = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const crumbs = [{ title: 'Trading Accounts - Live', link: links.recordsTradingAccounts.url }]

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })

  const [tradingAccounts, setTradingAccounts] = useState<TradingAccountDto[]>([])
  const [columns, setColumns] = useState<GridColumn[]>([])

  const [isClientModalOpen, setClientModalOpen] = useState<boolean>(false)
  const [isWalletModalOpen, setWalletModalOpen] = useState<boolean>(false)
  const [isIntroducingBrokersModalOpen, setIntroducingBrokersModalOpen] = useState<boolean>(false)
  const [clientModalFilterId, setClientModalFilterId] = useState<string>()
  const [walletModalFilterId, setWalletModalFilterId] = useState<string>()
  const [introducingBrokerFilterId, setIntroducingBrokerFilterId] = useState<string>()

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    leverageType,
    platformTypes,
    servers,
    bookType,
    tradingAccountStatus,
    tradingAccountGroup,
    tradingAccountType,
    tradingAccountGroupType,
    tickmillCompanies,
    gridFilterByFilterIdSelector,
  } = useMapStateToProps()

  const {
    fetchTradingAccountsLeverageType,
    fetchPlatformTypes,
    fetchServers,
    fetchBookTypes,
    fetchTradingAccountGroups,
    fetchTradingAccountGroupType,
    fetchTradingAccountStatus,
    fetchTickmillCompanies,
    fetchTradingAccountType,
  } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (gridFilterState: GridFilterActionState): Promise<PageData<TradingAccountDto[]>> => {
          const searchQuery = getQuery(gridFilterState.filter)
          const sortQuery = getSort(gridFilterState.sort)
          const response = await tradingAccountsApiClient.getTradingAccounts({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.pageSize,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(tradingAccountGroup)) await fetchTradingAccountGroups()
          if (isEmpty(leverageType)) await fetchTradingAccountsLeverageType()
          if (isEmpty(platformTypes)) await fetchPlatformTypes()
          if (isEmpty(servers)) await fetchServers()
          if (isEmpty(bookType)) await fetchBookTypes()
          if (isEmpty(tradingAccountStatus)) await fetchTradingAccountStatus()
          if (isEmpty(tradingAccountType)) await fetchTradingAccountType()
          if (isEmpty(tradingAccountGroupType)) await fetchTradingAccountGroupType()
          if (isEmpty(tickmillCompanies)) await fetchTickmillCompanies()

          setColumns(liveTradingAccountColumns)
          if (response?.items) {
            setTradingAccounts(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<TradingAccountDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(normalizeTradingAccountsSearchQuery(filter ?? gridState.filter)) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(normalizeTradingAccountsSortQuery(sortDescriptors ?? gridState.sort))
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSort(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getTradingAccounts({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTradingAccounts(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleClientModalOpen = (event: SelectClientModalOpenEvent) => {
    setClientModalOpen(true)
    setClientModalFilterId(event.filterId)
  }

  const handleClientModalClose = () => {
    setClientModalOpen(false)
  }

  const handleWalletModalOpen = (event: SelectWalletModalOpenEvent) => {
    setWalletModalOpen(true)
    setWalletModalFilterId(event.filterId)
  }

  const handleWalletModalClose = () => {
    setWalletModalOpen(false)
  }

  const handleIBModalOpen = (event: SelectIntroducingBrokerModalOpenEvent) => {
    setIntroducingBrokersModalOpen(true)
    setIntroducingBrokerFilterId(event.filterId)
  }

  const handleIBModalClose = () => {
    setIntroducingBrokersModalOpen(false)
  }

  const handleSelectedClients = (event: SelectClientEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSelectedWallets = (event: SelectWalletEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleIBSelect = (event: SelectIntroducingBrokerEvent) => {
    const filter = event?.filterId ? gridFilterByFilterIdSelector(event.filterId) : null
    if (filter) {
      gridActions.addGridFiltersByFilterId({
        ...filter,
        id: event.filterId,
        value: event.value,
      })
    }
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await tradingAccountsApiClient.getTradingAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getTradingAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })

    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await tradingAccountsApiClient.getTradingAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTradingAccounts(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await tradingAccountsApiClient.getTradingAccounts({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setTradingAccounts(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleColumnsSubmit = (columnsState: GridColumn[]) => {
    setColumns(columnsState)
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isClientModalOpen && (
        <Dialog open={isClientModalOpen} onOpenChange={(open) => !open && handleClientModalClose()}>
          <SelectClientTable
            filterId={clientModalFilterId}
            onSelect={handleSelectedClients}
            onClose={handleClientModalClose}
          />
        </Dialog>
      )}
      {isWalletModalOpen && (
        <Modal width={900} visible={isWalletModalOpen} onClose={handleWalletModalClose}>
          <SelectWalletTable
            filterId={walletModalFilterId}
            onSelect={handleSelectedWallets}
            onClose={handleWalletModalClose}
          />
        </Modal>
      )}
      {isIntroducingBrokersModalOpen && (
        <IntroducingBrokersModal
          filterId={introducingBrokerFilterId}
          onSelect={handleIBSelect}
          onClose={handleIBModalClose}
        />
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.TRADING_ACCOUNT}
        filterFields={[
          ...handleFilters(handleClientModalOpen, handleWalletModalOpen, handleIBModalOpen),
          ...getTradingAccountFilterFields(),
        ]}
        containerClassName='wallet-table__filter'
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={tradingAccounts}>
          <Grid
            {...gridState}
            resizable
            data={tradingAccounts}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            pageable={true}
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column
                  key={idx + 2}
                  columnMenu={(gridProps) => (
                    <CustomColumnMenu
                      {...gridProps}
                      columns={columns}
                      onColumnsSubmit={handleColumnsSubmit}
                    />
                  )}
                  {...column}
                />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const handleFilters = (
  handleClientModalOpen: (event: SelectClientModalOpenEvent) => void,
  handleWalletModalOpen: (event: SelectWalletModalOpenEvent) => void,
  handleIBModalOpen: (event: SelectWalletModalOpenEvent) => void
) => [
  {
    name: TradingAccountFilterKey.client,
    label: 'Client',
    filter: (clientFilter: TextFilterProps) => (
      <SelectClientFilter {...clientFilter} onClientModalOpen={handleClientModalOpen} />
    ),
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TradingAccountFilterKey.wallet,
    label: 'Wallet',
    filter: (walletFilter: TextFilterProps) => (
      <SelectWalletFilter {...walletFilter} onWalletModalOpen={handleWalletModalOpen} />
    ),
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
  {
    name: TradingAccountFilterKey.introducingBroker,
    label: 'Introducing Broker',
    filter: (ibFilter: TextFilterProps) => (
      <SelectIntroducingBrokersFilter
        {...ibFilter}
        onIntroducingBrokerModalOpen={handleIBModalOpen}
      >
        Choose IB
      </SelectIntroducingBrokersFilter>
    ),
    operators: [
      {
        text: GridFilterOperatorNameEnum.equalOperator,
        operator: GridFilterOperatorEnum.equalOperator,
      },
      {
        text: GridFilterOperatorNameEnum.notEqOperator,
        operator: GridFilterOperatorEnum.notEqOperator,
      },
    ],
  },
]
